/* Alles wat de projectpagina over één project leest, behalve bestanden en de
 * monitor (bestanden.ts, monitor.ts). Met de sessie van de gebruiker: RLS beslist.
 *
 * Elke functie krijgt de client als argument, zodat de toetsen hem kunnen
 * nabootsen. Een tabel die de gebruiker niet mag lezen (project_notes is
 * admin-only) geeft hier een lege lijst, geen fout: het logboek toont dan de
 * rest. Een echte fout op het project zelf gooit wel. */

export interface PaginaClient {
  from(tabel: string): any;
  rpc(naam: string, args?: Record<string, unknown>): any;
  auth: { getUser(): Promise<{ data: { user: { id: string } | null } }> };
}

export interface PaginaProject {
  id: string;
  name: string;
  project_number: string | null;
  type: string | null;
  stage: string | null;
  status: string | null;
  amount: number | null;
  probability: number | null;
  close_date: string | null;
  city: string | null;
  org_id: string | null;
  owner_id: string | null;
  sharepoint_folder_path: string | null;
  sharepoint_web_url: string | null;
  sharepoint_main_url: string | null;
  organization: { id: string; name: string } | null;
  owner: { id: string; display_name: string | null; email: string | null } | null;
  /** De volledige rij, voor ProjectModal (bewerken). */
  rij: Record<string, unknown>;
}

export async function haalProject(client: PaginaClient, id: string): Promise<PaginaProject | null> {
  const { data: p, error } = await client.from('projects').select('*').eq('id', id).is('deleted_at', null).maybeSingle();
  if (error) throw error;
  if (!p) return null;
  const [org, owner] = await Promise.all([
    p.org_id
      ? client.from('organizations').select('id, name').eq('id', p.org_id).maybeSingle()
      : Promise.resolve({ data: null }),
    p.owner_id
      ? client.from('profiles').select('id, display_name, email').eq('id', p.owner_id).maybeSingle()
      : Promise.resolve({ data: null }),
  ]);
  return {
    id: p.id,
    name: p.name,
    project_number: p.project_number ?? null,
    type: p.type ?? null,
    stage: p.stage ?? null,
    status: p.status ?? null,
    amount: p.amount === null || p.amount === undefined ? null : Number(p.amount),
    probability: p.probability ?? null,
    close_date: p.close_date ?? null,
    city: p.city ?? (p.data as any)?.city ?? null,
    org_id: p.org_id ?? null,
    owner_id: p.owner_id ?? null,
    sharepoint_folder_path: p.sharepoint_folder_path ?? null,
    sharepoint_web_url: p.sharepoint_web_url ?? null,
    sharepoint_main_url: p.sharepoint_main_url ?? null,
    organization: org?.data ?? null,
    owner: owner?.data ?? null,
    rij: p,
  };
}

export type ProjectWijziging = Partial<Pick<PaginaProject, 'stage' | 'amount' | 'probability' | 'close_date'>>;

export async function wijzigProject(client: PaginaClient, id: string, wijziging: ProjectWijziging): Promise<void> {
  const { error } = await client.from('projects').update(wijziging).eq('id', id);
  if (error) throw error;
}

/* ── offertes ─────────────────────────────────────────────────────── */

export interface Offerte {
  id: string;
  quote_number: string | null;
  title: string | null;
  status: string;
  total_amount: number | null;
  current_version: number | null;
  quote_date: string | null;
  valid_until: string | null;
  updated_at: string | null;
  /** Namen van de PDF's die als revisie van deze offerte in de projectmap staan (migratie 097). */
  bestanden: string[];
}

export async function haalOffertes(client: PaginaClient, projectId: string): Promise<Offerte[]> {
  const basis = 'id, quote_number, title, status, total_amount, current_version, quote_date, valid_until, updated_at';
  const vraag = (kolommen: string) =>
    client.from('quotes').select(kolommen).eq('project_id', projectId).is('deleted_at', null).order('updated_at', { ascending: false });
  let { data, error } = await vraag(`${basis}, revisies:quote_versions(file_name)`);
  // Zolang migratie 097 er niet is, kent quote_versions geen file_name: dan zonder.
  if (error?.code === '42703') ({ data, error } = await vraag(basis));
  if (error) throw error;
  return (data ?? []).map(({ revisies, ...q }: any) => ({
    ...q,
    total_amount: q.total_amount === null ? null : Number(q.total_amount),
    bestanden: (revisies ?? []).map((r: any) => r.file_name).filter(Boolean),
  }));
}

/** Een lege offerte aan dit project, met het volgende offertenummer. Geeft het id terug. */
export async function maakOfferte(client: PaginaClient, project: PaginaProject): Promise<string> {
  if (!project.org_id) throw new Error('Koppel eerst een organisatie aan dit project; een offerte hoort bij een klant.');
  const { data: nummer, error: nummerFout } = await client.rpc('generate_quote_number');
  if (nummerFout) throw nummerFout;
  const { data: { user } } = await client.auth.getUser();
  if (!user) throw new Error('Niet ingelogd.');
  const { data, error } = await client
    .from('quotes')
    .insert({ title: project.name, organization_id: project.org_id, project_id: project.id, quote_number: nummer, owner_id: user.id })
    .select('id')
    .single();
  if (error) throw error;
  return data.id;
}

/* ── gekoppeld ────────────────────────────────────────────────────── */

export interface Gekoppeld {
  businesscase: { id: string; name: string | null; nettoInvestering: number | null; investering: number | null } | null;
  terreinen: { id: string; name: string | null; ontwerpen: number; updated_at: string | null }[];
  subsidies: { id: string; naam: string | null; volledigheid: number | null; updated_at: string | null }[];
  inspecties: { aantal: number; laatste: { date: string | null; type: string | null; notes: string | null } | null };
  site: { id: string; name: string | null } | null;
}

const leeg = <T>(p: PromiseLike<{ data: T | null; error: unknown }>, terug: T): Promise<T> =>
  Promise.resolve(p).then((r) => (r.error ? terug : r.data ?? terug), () => terug);

export async function haalGekoppeld(client: PaginaClient, projectId: string): Promise<Gekoppeld> {
  const [bcs, terreinen, subsidies, inspecties, sites] = await Promise.all([
    leeg<any[]>(
      client.from('businesscases').select('*').eq('project_id', projectId).eq('is_archived', false).order('created_at', { ascending: false }).limit(5),
      [],
    ),
    leeg<any[]>(client.from('laadplein_designs').select('id, name, updated_at').eq('project_id', projectId), []),
    leeg<any[]>(client.from('sub_intakes').select('id, naam, volledigheid_pct, updated_at').eq('project_id', projectId), []),
    leeg<any[]>(client.from('inspections').select('id, date, type, notes').eq('project_id', projectId).order('date', { ascending: false }), []),
    leeg<any[]>(client.from('sites').select('id, name').eq('project_id', projectId), []),
  ]);

  const ontwerpen = terreinen.length
    ? await leeg<any[]>(
      client.from('laadplein_design_scenarios').select('id, design_id').in('design_id', terreinen.map((t) => t.id)),
      [],
    )
    : [];

  /* De nieuwste businesscase met cijfers; een lege (alles nul) telt niet als uitkomst. */
  const bc = bcs.find((b) => Number(b.total_investment) > 0) ?? bcs[0] ?? null;
  return {
    businesscase: bc
      ? {
        id: bc.id,
        name: bc.name ?? null,
        nettoInvestering: bc.net_investment === null || bc.net_investment === undefined ? null : Number(bc.net_investment),
        investering: bc.total_investment === null || bc.total_investment === undefined ? null : Number(bc.total_investment),
      }
      : null,
    terreinen: terreinen.map((t) => ({
      id: t.id,
      name: t.name ?? null,
      ontwerpen: ontwerpen.filter((o) => o.design_id === t.id).length,
      updated_at: t.updated_at ?? null,
    })),
    subsidies: subsidies.map((s) => ({ id: s.id, naam: s.naam ?? null, volledigheid: s.volledigheid_pct ?? null, updated_at: s.updated_at ?? null })),
    inspecties: { aantal: inspecties.length, laatste: inspecties[0] ? { date: inspecties[0].date, type: inspecties[0].type, notes: inspecties[0].notes } : null },
    site: sites[0] ? { id: sites[0].id, name: sites[0].name ?? null } : null,
  };
}

/* ── logboek ──────────────────────────────────────────────────────── */

export type LogSoort = 'notitie' | 'taak' | 'mijlpaal' | 'versie' | 'inspectie' | 'mail';

export interface LogItem {
  id: string;
  soort: LogSoort;
  /** ISO-datum of -tijdstip waarop het item in de tijd staat. */
  datum: string;
  titel: string;
  tekst: string | null;
  /** Taak: open of klaar. Mijlpaal: status uit project_milestones. */
  status?: string | null;
}

export interface Logboek {
  openTaken: { id: string; title: string; due_date: string | null }[];
  items: LogItem[];
}

export async function haalLogboek(client: PaginaClient, projectId: string): Promise<Logboek> {
  const [notes, todos, milestones, inspecties, versies, mails] = await Promise.all([
    leeg<any[]>(client.from('project_notes').select('id, title, body, note_type, created_at').eq('project_id', projectId).order('created_at', { ascending: false }).limit(100), []),
    leeg<any[]>(client.from('unified_todos').select('id, title, status, due_date, created_at, completed_at').eq('project_id', projectId).order('created_at', { ascending: false }).limit(100), []),
    leeg<any[]>(client.from('project_milestones').select('id, title, description, milestone_date, status').eq('project_id', projectId), []),
    leeg<any[]>(client.from('inspections').select('id, date, type, notes').eq('project_id', projectId), []),
    leeg<any[]>(client.from('project_file_versions').select('id, file_name, version, message, previous_file_name, created_at').eq('project_id', projectId).order('created_at', { ascending: false }).limit(100), []),
    leeg<any[]>(client.from('email_inbox').select('id, subject, from_email, received_at').eq('matched_project_id', projectId).order('received_at', { ascending: false }).limit(50), []),
  ]);

  /* Een nieuwe versie via gv-crm-mcp schrijft ook een notitie "Nieuwe versie: …";
     die staat al als versie in het logboek, dus niet twee keer. */
  const versieNamen = new Set(versies.map((v) => `Nieuwe versie: ${v.file_name}`));

  const items: LogItem[] = [
    ...notes
      .filter((n) => !versieNamen.has(n.title))
      .map((n): LogItem => ({ id: `n:${n.id}`, soort: 'notitie', datum: n.created_at, titel: n.title, tekst: n.body ?? null })),
    ...todos
      .filter((t) => t.status === 'completed')
      .map((t): LogItem => ({ id: `t:${t.id}`, soort: 'taak', datum: t.completed_at ?? t.created_at, titel: t.title, tekst: null, status: 'klaar' })),
    ...milestones
      .filter((m) => m.milestone_date)
      .map((m): LogItem => ({ id: `m:${m.id}`, soort: 'mijlpaal', datum: m.milestone_date, titel: m.title, tekst: m.description ?? null, status: m.status })),
    ...inspecties
      .filter((i) => i.date)
      .map((i): LogItem => ({ id: `i:${i.id}`, soort: 'inspectie', datum: i.date, titel: `Inspectie ${i.type ?? ''}`.trim(), tekst: i.notes ?? null })),
    ...versies.map((v): LogItem => ({
      id: `v:${v.id}`,
      soort: 'versie',
      datum: v.created_at,
      titel: v.previous_file_name ? `Nieuwe versie: ${v.file_name}` : `Bestand: ${v.file_name}`,
      tekst: v.message ?? null,
    })),
    ...mails.map((m): LogItem => ({ id: `e:${m.id}`, soort: 'mail', datum: m.received_at, titel: m.subject || '(geen onderwerp)', tekst: m.from_email ?? null })),
  ].sort((a, b) => (a.datum < b.datum ? 1 : a.datum > b.datum ? -1 : 0));

  return {
    openTaken: todos.filter((t) => t.status !== 'completed').map((t) => ({ id: t.id, title: t.title, due_date: t.due_date ?? null })),
    items,
  };
}

export async function voegNotitieToe(client: PaginaClient, projectId: string, tekst: string): Promise<void> {
  const t = tekst.trim();
  const eersteRegel = t.split('\n')[0]!.slice(0, 120);
  const { error } = await client.from('project_notes').insert({ project_id: projectId, title: eersteRegel, body: t, note_type: 'note' });
  if (error) throw error;
}

export async function voegTaakToe(client: PaginaClient, projectId: string, titel: string): Promise<void> {
  const { data: { user } } = await client.auth.getUser();
  if (!user) throw new Error('Niet ingelogd.');
  const { error } = await client.from('unified_todos').insert({
    title: titel.trim(),
    project_id: projectId,
    assigned_to: user.id,
    created_by: user.id,
    scope: 'team_crm',
    is_shared: true,
  });
  if (error) throw error;
}

/** Een mijlpaal op een datum; in het verleden of vandaag telt hij als afgerond. */
export async function voegMijlpaalToe(client: PaginaClient, projectId: string, titel: string, datum: string, vandaag: string): Promise<void> {
  const { data: { user } } = await client.auth.getUser();
  const { error } = await client.from('project_milestones').insert({
    project_id: projectId,
    title: titel.trim(),
    milestone_date: datum,
    milestone_type: 'general',
    status: datum <= vandaag ? 'completed' : 'pending',
    created_by: user?.id ?? null,
  });
  if (error) throw error;
}

export async function rondTaakAf(client: PaginaClient, taakId: string): Promise<void> {
  const { error } = await client.from('unified_todos').update({ status: 'completed', completed_at: new Date().toISOString() }).eq('id', taakId);
  if (error) throw error;
}
